import { useMemo } from "react";

/**
 * Compact "what ran on this call" chip row, hydrated from the
 * `calls.model_snapshot` JSON column the bot writes at call start.
 *
 * Renders nothing when the snapshot is missing (older calls) or malformed,
 * so it's safe to drop into any detail header without conditional checks.
 */
export function CallModelStack({
  snapshot,
}: {
  snapshot: string | null | undefined;
}) {
  const chips = useMemo(() => buildChips(snapshot), [snapshot]);
  if (chips.length === 0) return null;
  return (
    <div id="call-model-stack" className="model-stack">
      {chips.map((c, i) => (
        <span
          id={`call-model-stack-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${i}`}
          key={`${c.label}-${i}`}
          className={`model-chip${c.kind ? ` model-chip-${c.kind}` : ""}`}
          title={c.title}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

type Chip = {
  label: string;
  /** Drives the colour style. */
  kind?: "provider" | "stage";
  title?: string;
};

function buildChips(raw: string | null | undefined): Chip[] {
  if (!raw) return [];
  let snap: Record<string, unknown>;
  try {
    snap = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!snap || typeof snap !== "object") return [];

  const chips: Chip[] = [];
  const provider = snap.provider;

  if (provider === "openai") {
    chips.push({ kind: "provider", label: "OpenAI Realtime" });
    if (typeof snap.model === "string") chips.push({ label: snap.model });
    if (typeof snap.voice === "string") chips.push({ label: `voice ${snap.voice}` });
  } else if (provider === "gemini") {
    chips.push({ kind: "provider", label: "Gemini Live" });
    if (typeof snap.model === "string") {
      const trimmed = snap.model.replace(/^models\//, "");
      chips.push({ label: trimmed, title: snap.model });
    }
    if (typeof snap.voice === "string") chips.push({ label: `voice ${snap.voice}` });
  } else if (provider === "chained") {
    chips.push({ kind: "provider", label: "Pipeline" });
    if (typeof snap.stt_model === "string") {
      chips.push({ kind: "stage", label: `STT ${snap.stt_model}` });
    }
    if (typeof snap.llm_model === "string") {
      chips.push({ kind: "stage", label: `LLM ${snap.llm_model}` });
    }
    if (typeof snap.tts_voice === "string") {
      // Cartesia voices are UUIDs — show the head + ellipsis to keep the
      // chip readable; full id lives in the title attribute.
      const v = snap.tts_voice;
      const short = v.length > 14 ? `${v.slice(0, 8)}…` : v;
      chips.push({ kind: "stage", label: `TTS ${short}`, title: v });
    }
  } else if (typeof provider === "string") {
    chips.push({ kind: "provider", label: provider });
  }

  return chips;
}
